import ListingFilterFields from "@/components/listing/ListingFilterFields";
import { Button } from "@/components/ui/button";
import { ListingFilters } from "@/lib/fetch/listing";
import { useTranslations } from "next-intl";
import React from "react";
import { SlidersHorizontal } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { twMerge } from "tailwind-merge";
import SearchInput from "../SearchInput";

type Props = {
  filters: ListingFilters;
  setFilters: (filters: ListingFilters) => void;
  onReset: () => void;
  countFilters: number;
  includeSearch?: boolean;
};

const MotionButton = motion(Button);

const SearchFilters: React.FC<Props> = ({
  filters,
  onReset,
  setFilters,
  countFilters,
  includeSearch = false,
}) => {
  const t = useTranslations("search");

  return (
    <div className="google-anno-skip flex w-full flex-col gap-5">
      <div className="flex min-h-14 flex-row items-center justify-between gap-4 border-b py-3 lg:px-5">
        <div className="flex flex-row items-center gap-2.5">
          <span className="flex size-9 items-center justify-center rounded-xl bg-secondary text-secondary-foreground">
            <SlidersHorizontal className="size-4" />
          </span>
          <h2 className="text-lg font-semibold text-foreground">
            {t("filters")}
          </h2>
          {countFilters > 0 && (
            <span className="rounded-full bg-primary px-2 py-0.5 text-xs font-bold tabular-nums text-primary-foreground">
              {countFilters}
            </span>
          )}
        </div>

        <AnimatePresence>
          {countFilters > 0 && (
            <MotionButton
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              type="button"
              variant="outline"
              size="sm"
              onClick={onReset}
            >
              {t("clear-all")}
            </MotionButton>
          )}
        </AnimatePresence>
      </div>
      {includeSearch && (
        <SearchInput
          search={filters.search || ""}
          setSearch={(search) => setFilters({ ...filters, search })}
        />
      )}
      <div className={twMerge("w-full", !includeSearch && "lg:px-5")}>
        <ListingFilterFields filters={filters} onChange={setFilters} />
      </div>
    </div>
  );
};

export default SearchFilters;
